/**
 * Runtime auditor — monitoreo continuo de salud de Map V2.
 *
 * Activación:
 *   localStorage.setItem('kalan:audit', '1')   → activa al boot (también activa
 *                                                automáticamente en DEV mode)
 *
 * Captura cada N segundos un snapshot de:
 *   - heap (performance.memory.usedJSHeapSize)
 *   - DOM nodes
 *   - iframes
 *   - jsEventListeners (estimación basada en getEventListeners si está disponible)
 *   - count de monitors por entityType
 *   - alerts por type
 *   - cadSubscriptions
 *   - _pendingTripData
 *   - _positionBuffer keys
 *   - animations activas
 *   - WS messages procesados (delta desde la última muestra)
 *   - long tasks observadas (delta)
 *   - sourceManager.features count por entityType
 *
 * Persiste samples en window.__auditor.samples (memoria).
 * Cada 5 min escribe automáticamente al localStorage `kalan:audit-last`.
 *
 * API global:
 *   window.__auditor.snapshot()        → toma sample manual (también lo guarda)
 *   window.__auditor.report()          → imprime tabla resumen + analiza tendencias
 *   window.__auditor.export()          → copia JSON al clipboard + lo retorna
 *   window.__auditor.exportCompact()   → copia JSON compacto (solo deltas)
 *   window.__auditor.compare(i, j)     → diff entre dos samples (índices)
 *   window.__auditor.start(intervalMs) → arranca/reinicia auto-snapshot
 *   window.__auditor.stop()            → detiene auto-snapshot
 *   window.__auditor.clear()           → vacía samples
 *
 * Atajos:
 *   Ctrl+Shift+A   → snapshot manual + export al clipboard
 *
 * Costo: 1 snapshot toma ~5-15 ms. Default cada 30s = despreciable.
 */

var AUDITOR_ENABLED = (function() {
  try {
    if (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.DEV) return true
  } catch (_) {}
  try {
    if (window.localStorage && window.localStorage.getItem('kalan:audit') === '1') return true
  } catch (_) {}
  return false
})()

var DEFAULT_INTERVAL_MS = 30000  // 30 s
var PERSIST_EVERY_N = 10         // cada 10 samples (= cada 5 min default) escribe a localStorage
var MAX_SAMPLES = 240             // 240 × 30s = 2 horas continuas. Después rota.

var state = {
  samples: [],
  startedAt: Date.now(),
  intervalId: null,
  intervalMs: DEFAULT_INTERVAL_MS,
  // Trackers acumulativos (deltas se calculan al snapshot)
  wsMessageCount: 0,
  longTaskCount: 0,
  longTaskTotalMs: 0,
  longTaskGT200Count: 0,
  longTaskGT200TotalMs: 0,
  prevWsCount: 0,
  prevLongCount: 0,
  prevLongMs: 0,
  prevLong200Count: 0,
  prevLong200Ms: 0,
  longTaskObserver: null,
}

function findMapV2() {
  try {
    var root = document.querySelector('#app')
    if (!root || !root.__vue__) return null
    function find(vm) {
      if (!vm) return null
      if (vm.alerts && vm._monitors) return vm
      if (vm.$children) {
        for (var i = 0; i < vm.$children.length; i++) {
          var r = find(vm.$children[i])
          if (r) return r
        }
      }
      return null
    }
    return find(root.__vue__)
  } catch (_) { return null }
}

function safeCount(obj) {
  try { return obj ? Object.keys(obj).length : 0 } catch (_) { return 0 }
}

function takeSnapshot(label) {
  var v2 = findMapV2()
  var sm = v2 && v2.sourceManager
  var sock = v2 && v2.socketIOHandler
  var heap = (performance.memory && performance.memory.usedJSHeapSize) || 0
  var heapTotal = (performance.memory && performance.memory.totalJSHeapSize) || 0

  // Deltas de trackers acumulativos
  var wsDelta = state.wsMessageCount - state.prevWsCount
  var ltDelta = state.longTaskCount - state.prevLongCount
  var ltDur = state.longTaskTotalMs - state.prevLongMs
  var lt200Delta = state.longTaskGT200Count - state.prevLong200Count
  var lt200Dur = state.longTaskGT200TotalMs - state.prevLong200Ms
  state.prevWsCount = state.wsMessageCount
  state.prevLongCount = state.longTaskCount
  state.prevLongMs = state.longTaskTotalMs
  state.prevLong200Count = state.longTaskGT200Count
  state.prevLong200Ms = state.longTaskGT200TotalMs

  // Conteos por entityType
  var monByType = {}
  var monitors = (v2 && v2._monitors) || {}
  var monKeys = Object.keys(monitors)
  for (var i = 0; i < monKeys.length; i++) {
    var et = monitors[monKeys[i]].entityType || '?'
    monByType[et] = (monByType[et] || 0) + 1
  }
  // Alerts por type
  var alertByType = {}
  var alerts = (v2 && v2.alerts) || []
  for (var a = 0; a < alerts.length; a++) {
    var t = alerts[a].type || '?'
    alertByType[t] = (alertByType[t] || 0) + 1
  }
  // Source manager features por entityType
  var featByType = {}
  if (sm && sm.features) {
    var iter = sm.features.values()
    var n = iter.next()
    while (!n.done) {
      var pet = (n.value && n.value.properties && n.value.properties.entityType) || '?'
      featByType[pet] = (featByType[pet] || 0) + 1
      n = iter.next()
    }
  }

  var s = {
    label: label || 'auto',
    t: new Date().toISOString(),
    tSec: Math.round((Date.now() - state.startedAt) / 1000),
    heap: {
      usedMB: Math.round(heap / 1048576),
      totalMB: Math.round(heapTotal / 1048576),
    },
    dom: {
      nodes: document.querySelectorAll('*').length,
      iframes: document.querySelectorAll('iframe').length,
      videos: document.querySelectorAll('video').length,
    },
    monitors: { total: monKeys.length, byType: monByType },
    alerts: { total: alerts.length, byType: alertByType },
    sourceFeatures: { total: sm && sm.features ? sm.features.size : 0, byType: featByType },
    cadSubs: sock && sock._cadSubscriptions ? sock._cadSubscriptions.size : 0,
    pendingTripData: safeCount(v2 && v2._pendingTripData),
    positionBufferKeys: safeCount(v2 && v2._positionBuffer),
    animations: (window.__animMetrics && window.__animMetrics.activeAnimations) || 0,
    wsMessages: { total: state.wsMessageCount, deltaSinceLast: wsDelta },
    longTasks: {
      gt50: { total: state.longTaskCount, deltaSinceLast: ltDelta, deltaMs: Math.round(ltDur) },
      gt200: { total: state.longTaskGT200Count, deltaSinceLast: lt200Delta, deltaMs: Math.round(lt200Dur) },
    },
  }
  state.samples.push(s)

  // Rotación: si excedimos MAX_SAMPLES, mantén los primeros 50 (boot) + últimos 190.
  if (state.samples.length > MAX_SAMPLES) {
    state.samples = state.samples.slice(0, 50).concat(state.samples.slice(-190))
  }

  // Persiste cada N samples a localStorage (resiste recargas para revisar histórico).
  if (state.samples.length % PERSIST_EVERY_N === 0) {
    try { window.localStorage.setItem('kalan:audit-last', JSON.stringify(state.samples)) } catch (_) {}
  }
  return s
}

function start(intervalMs) {
  if (intervalMs) state.intervalMs = intervalMs
  if (state.intervalId) clearInterval(state.intervalId)
  state.intervalId = setInterval(function() { takeSnapshot('auto') }, state.intervalMs)
  if (AUDITOR_ENABLED) console.log('[auditor] auto-snapshot cada ' + state.intervalMs + 'ms (samples: ' + state.samples.length + ')')
}

function stop() {
  if (state.intervalId) { clearInterval(state.intervalId); state.intervalId = null }
}

function clear() {
  state.samples = []
  state.startedAt = Date.now()
  state.wsMessageCount = 0
  state.longTaskCount = 0
  state.longTaskTotalMs = 0
  state.longTaskGT200Count = 0
  state.longTaskGT200TotalMs = 0
  state.prevWsCount = 0
  state.prevLongCount = 0
  state.prevLongMs = 0
  state.prevLong200Count = 0
  state.prevLong200Ms = 0
}

function report() {
  if (state.samples.length === 0) {
    console.log('[auditor] sin samples. Llama __auditor.snapshot() o espera.')
    return null
  }
  var first = state.samples[0]
  var last = state.samples[state.samples.length - 1]
  console.log('[auditor] ' + state.samples.length + ' samples / ' + Math.round((Date.now() - state.startedAt)/60000) + ' min')
  console.log('  heap:        ' + first.heap.usedMB + 'MB → ' + last.heap.usedMB + 'MB  (Δ ' + (last.heap.usedMB - first.heap.usedMB) + ')')
  console.log('  dom.nodes:   ' + first.dom.nodes + ' → ' + last.dom.nodes + '  (Δ ' + (last.dom.nodes - first.dom.nodes) + ')')
  console.log('  iframes:     ' + first.dom.iframes + ' → ' + last.dom.iframes)
  console.log('  monitors:    ' + first.monitors.total + ' → ' + last.monitors.total + '  (Δ ' + (last.monitors.total - first.monitors.total) + ')')
  console.log('  alerts:      ' + first.alerts.total + ' → ' + last.alerts.total + '  (Δ ' + (last.alerts.total - first.alerts.total) + ')')
  console.log('  cadSubs:     ' + first.cadSubs + ' → ' + last.cadSubs + '  (Δ ' + (last.cadSubs - first.cadSubs) + ')')
  console.log('  pendingTrip: ' + first.pendingTripData + ' → ' + last.pendingTripData + '  (Δ ' + (last.pendingTripData - first.pendingTripData) + ')')
  console.log('  posBuffer:   ' + first.positionBufferKeys + ' → ' + last.positionBufferKeys + '  (Δ ' + (last.positionBufferKeys - first.positionBufferKeys) + ')')
  console.log('  ws msgs:     total ' + last.wsMessages.total)
  console.log('  long tasks:  ' + last.longTasks.gt50.total + ' (>50ms) / ' + last.longTasks.gt200.total + ' (>200ms)')

  // Diff por entityType
  console.log('\nMonitors por type (last):')
  console.table(last.monitors.byType)
  console.log('Alerts por type (last):')
  console.table(last.alerts.byType)

  return { first: first, last: last, samples: state.samples.length }
}

function exportFull() {
  var json = JSON.stringify({
    env: {
      userAgent: navigator.userAgent,
      url: location.href,
      startedAt: state.startedAt,
      intervalMs: state.intervalMs,
    },
    samples: state.samples,
  }, null, 2)
  if (navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard.writeText(json).then(function() {
      console.log('[auditor] export full copiado al clipboard (' + json.length + ' chars, ' + state.samples.length + ' samples)')
    })
  } else {
    var ta = document.createElement('textarea')
    ta.value = json; document.body.appendChild(ta); ta.select()
    try { document.execCommand('copy') } catch (_) {}
    document.body.removeChild(ta)
    console.log('[auditor] export full copiado (fallback)')
  }
  return state.samples
}

function exportCompact() {
  // Compacto: solo first, last, y deltas entre el primero y cada N-th sample.
  var rows = []
  if (state.samples.length === 0) {
    console.log('[auditor] sin samples'); return []
  }
  var first = state.samples[0]
  for (var i = 0; i < state.samples.length; i++) {
    var s = state.samples[i]
    rows.push({
      tSec: s.tSec,
      label: s.label,
      heapMB: s.heap.usedMB,
      heapDelta: s.heap.usedMB - first.heap.usedMB,
      nodes: s.dom.nodes,
      nodesDelta: s.dom.nodes - first.dom.nodes,
      iframes: s.dom.iframes,
      monitors: s.monitors.total,
      monDelta: s.monitors.total - first.monitors.total,
      alerts: s.alerts.total,
      alertsDelta: s.alerts.total - first.alerts.total,
      reportants: (s.monitors.byType.reportant || 0),
      cadSubs: s.cadSubs,
      pendingTrip: s.pendingTripData,
      posBuffer: s.positionBufferKeys,
      wsTotal: s.wsMessages.total,
      wsDelta: s.wsMessages.deltaSinceLast,
      longTaskGT50: s.longTasks.gt50.deltaSinceLast,
      longTaskMs: s.longTasks.gt50.deltaMs,
    })
  }
  var json = JSON.stringify(rows, null, 2)
  if (navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard.writeText(json).then(function() {
      console.log('[auditor] export compacto copiado (' + rows.length + ' filas, ' + json.length + ' chars)')
    })
  }
  console.table(rows.length <= 30 ? rows : rows.slice(-30))
  return rows
}

function compare(i, j) {
  if (i == null) i = 0
  if (j == null) j = state.samples.length - 1
  var a = state.samples[i]
  var b = state.samples[j]
  if (!a || !b) { console.log('[auditor] indices invalidos'); return null }
  var diff = {
    span_min: ((b.tSec - a.tSec) / 60).toFixed(1),
    heapMB: { a: a.heap.usedMB, b: b.heap.usedMB, delta: b.heap.usedMB - a.heap.usedMB },
    nodes: { a: a.dom.nodes, b: b.dom.nodes, delta: b.dom.nodes - a.dom.nodes },
    iframes: { a: a.dom.iframes, b: b.dom.iframes, delta: b.dom.iframes - a.dom.iframes },
    monitors: { a: a.monitors.total, b: b.monitors.total, delta: b.monitors.total - a.monitors.total },
    alerts: { a: a.alerts.total, b: b.alerts.total, delta: b.alerts.total - a.alerts.total },
    reportants: { a: a.monitors.byType.reportant || 0, b: b.monitors.byType.reportant || 0 },
    cadSubs: { a: a.cadSubs, b: b.cadSubs, delta: b.cadSubs - a.cadSubs },
    pendingTrip: { a: a.pendingTripData, b: b.pendingTripData, delta: b.pendingTripData - a.pendingTripData },
    posBuffer: { a: a.positionBufferKeys, b: b.positionBufferKeys, delta: b.positionBufferKeys - a.positionBufferKeys },
    wsTotalIncrement: b.wsMessages.total - a.wsMessages.total,
  }
  diff.reportants.delta = diff.reportants.b - diff.reportants.a
  console.table(diff)
  return diff
}

// LongTask observer — captura tasks > 50ms en background
;(function setupLongTaskObserver() {
  if (typeof PerformanceObserver === 'undefined') return
  try {
    state.longTaskObserver = new PerformanceObserver(function(list) {
      var entries = list.getEntries()
      for (var i = 0; i < entries.length; i++) {
        var dur = entries[i].duration
        state.longTaskCount++
        state.longTaskTotalMs += dur
        if (dur > 200) {
          state.longTaskGT200Count++
          state.longTaskGT200TotalMs += dur
        }
      }
    })
    state.longTaskObserver.observe({ entryTypes: ['longtask'] })
  } catch (_) {}
})()

// Hook al ws.onmessage del WS principal — incrementa contador.
// Lo hookeamos lazy en el primer snapshot porque socketIOHandler/wsHandler
// no existen al boot del módulo.
function hookWsCounters() {
  var v2 = findMapV2()
  if (!v2) return
  var ws = v2.wsHandler
  if (ws && !ws._auditorHooked && ws.ws) {
    var orig = ws.ws.onmessage
    if (orig) {
      ws.ws.onmessage = function(ev) {
        state.wsMessageCount++
        return orig.call(ws.ws, ev)
      }
      ws._auditorHooked = true
    }
  }
  // Trip socket
  var trip = v2.socketIOHandler && v2.socketIOHandler.trip
  if (trip && !trip._auditorHooked) {
    var origUpdate = trip.listeners('update')[0]
    var origSubd = trip.listeners('subscribed')[0]
    if (origUpdate) {
      trip.off('update', origUpdate)
      trip.on('update', function(d) { state.wsMessageCount++; return origUpdate(d) })
    }
    if (origSubd) {
      trip.off('subscribed', origSubd)
      trip.on('subscribed', function(d) {
        if (Array.isArray(d)) state.wsMessageCount += d.length
        else state.wsMessageCount++
        return origSubd(d)
      })
    }
    trip._auditorHooked = true
  }
}

// Auto-arranque si está habilitado
if (AUDITOR_ENABLED) {
  // Damos 5s para que MapV2 termine de inicializarse antes del primer snapshot
  setTimeout(function() {
    hookWsCounters()
    takeSnapshot('boot')
    start(DEFAULT_INTERVAL_MS)
    // Re-hook después de 10s adicional por si el WS connect llega tarde
    setTimeout(hookWsCounters, 10000)
  }, 5000)
}

// Atajo Ctrl+Shift+A
if (typeof document !== 'undefined') {
  document.addEventListener('keydown', function(e) {
    if (e.ctrlKey && e.shiftKey && (e.key === 'A' || e.key === 'a')) {
      e.preventDefault()
      hookWsCounters()
      takeSnapshot('manual')
      exportFull()
    }
  })
}

// Exponer API global
window.__auditor = {
  samples: state.samples,
  snapshot: function(label) { hookWsCounters(); return takeSnapshot(label || 'manual') },
  report: report,
  export: exportFull,
  exportCompact: exportCompact,
  compare: compare,
  start: function(ms) { hookWsCounters(); start(ms) },
  stop: stop,
  clear: clear,
  state: state,
}

console.log('[auditor] module loaded (' + (AUDITOR_ENABLED ? 'ENABLED — auto-start en 5s' : 'disabled — set localStorage.kalan:audit=1') + ')')
